import React, {useEffect, useMemo, useState} from "react";
import {useProjects} from "../../ProjectsContext";
import "./WorksManager.css";

const API_URL = "/api/categories.php";

function getFact(project, labels) {
    const fact = (project.facts ?? []).find(f =>
        labels.some(label =>
            f.label?.trim().toLowerCase() === label.toLowerCase()
        )
    );

    return fact?.value?.trim() ?? "";
}

function getYear(project) {
    const value = getFact(project, ["Jahr", "Baujahr", "Projektjahr"]);
    const match = value.match(/\b(?:19|20)\d{2}\b/);

    return match ? Number(match[0]) : null;
}

export default function WorksManager({onClose, onSelectProject, initialCategoryId = null}) {
    const {projects} = useProjects();
    const [categories, setCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(initialCategoryId);

    const handleCategoryClick = (categoryId) => {
        setSelectedCategoryId(categoryId);
    };

    useEffect(() => {
        const controller = new AbortController();

        async function loadCategories() {
            try {
                const response = await fetch(API_URL, {
                    credentials: "include",
                    signal: controller.signal
                });

                if (!response.ok) {
                    throw new Error("Kategorien konnten nicht geladen werden");
                }

                const data = await response.json();
                setCategories(Array.isArray(data) ? data : []);
            } catch (error) {
                if (error.name !== "AbortError") {
                    console.error(error);
                }
            }
        }

        loadCategories();

        return () => controller.abort();
    }, []);

    useEffect(() => {
        setSelectedCategoryId(initialCategoryId);
    }, [initialCategoryId]);

    const sortedProjects = useMemo(() => {
        return [...(projects ?? [])].sort((a, b) => {
            const yearA = getYear(a);
            const yearB = getYear(b);

            if (yearA === null && yearB === null) return 0;
            if (yearA === null) return 1;
            if (yearB === null) return -1;

            return yearB - yearA;
        });
    }, [projects]);

    const filteredProjects = useMemo(() => {
        if (selectedCategoryId == null) {
            return sortedProjects;
        }

        return sortedProjects.filter(project =>
            (project.categories ?? []).some(
                id => String(id) === String(selectedCategoryId)
            )
        );
    }, [sortedProjects, selectedCategoryId]);

    const getProjectCategories = project =>
        categories.filter(category =>
            (project.categories ?? []).includes(category.id)
        );

    const handleProjectClick = project => {
        onSelectProject(project);
    };

    return (
        <div className="works-overlay" onClick={onClose}>
            <div
                className="works-manager"
                onClick={e => e.stopPropagation()}
            >
                <div className="works-header">

                    <div className="works-header-actions">
                        <button
                            type="button"
                            className="works-close"
                            onClick={onClose}
                            aria-label="Schließen"
                        >
                            ×
                        </button>
                    </div>
                </div>

                <div className="works-table">
                    <div className="works-table-header">
                        <span>Projekt</span>
                        <span>Stadt</span>
                        <span>Jahr</span>
                        <span>Kategorie</span>
                    </div>

                    <div className="works-table-body">
                        {filteredProjects.map(project => {
                            const city = getFact(project, ["Ort", "Stadt"]);
                            const year = getYear(project);
                            const projectCategories = getProjectCategories(project);

                            return (
                                <div key={project.id} className="works-row" onClick={() => handleProjectClick(project)}>
                                    <span className="works-title" title={project.title}>{project.title}</span>
                                    <span className="works-city">{city || "—"}</span>
                                    <span className="works-year">{year ?? "—"}</span>
                                    <span className="works-category-list">

                            {projectCategories.map(category => (
                                <button
                                    key={category.id}
                                    type="button"
                                    className="works-categories"
                                    style={{
                                        borderBottom: `2px solid ${category.color}`,
                                        color: category.color
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCategoryClick(category.id);
                                    }}
                                    title={`Alle Projekte in ${category.name} anzeigen`}
                                >
                                    {category.name}
                                </button>
                            ))}
                              </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
